// Arten der Einträge im Druckprotokoll, lesbar und eingeordnet – eine Quelle für Verwaltung → Rollen (rollTracking.js)
// und Verwaltung → Druckprotokoll (05.11.2026). Die Drucker-App lädt neben den Verkäufen (ticket_sale) ein
// Druckprotokoll hoch (bconn.print_log.print_type, Migration 32 in bconn-aws); ticketsale?option=printLog liefert beides
// mit kind: VERKAUF bzw. STORNO für Verkäufe, sonst print_type unverändert.
//   Drucke ohne Verkauf (PRINT_TYPES): verbrauchen Papier – in der Rollenauswertung belegen sie Abschnitte wie ein
//     Verkauf bzw. erklären ohne Rollennummer eine Lücke. Nur diese feste Liste.
//   Positionsmeldungen (POSITION_TYPES): drucken nichts, melden die Stelle der Rolle (GeBE).
//   Druckerzustände (STATE_TYPES, App ab 3.0.6): drucken nichts, verbrauchen kein Papier, meist ohne Rollennummer; die
//     Rollenauswertung ignoriert sie wie jede künftige, hier noch unbekannte Art.

export const SALE_KINDS = Object.freeze({
  VERKAUF: 'Verkauf',
  STORNO: 'Storno'
});

export const PRINT_TYPES = Object.freeze({
  TESTDRUCK: 'Testdruck',
  ABRECHNUNG: 'Abrechnung',
  FAHRPLAN_LINIE: 'Fahrplan (Linie)',
  FAHRPLAN_HALTESTELLE: 'Fahrplan (Haltestelle)',
  DIENSTPLAN: 'Dienstplan',
  DOKUMENT: 'Dokument'
});

export const POSITION_TYPES = Object.freeze({
  VERBINDUNG: 'Drucker verbunden',
  PAPIEREINZUG: 'Papiereinzug'
});

export const STATE_TYPES = Object.freeze({
  DECKEL_OFFEN: 'Deckel geöffnet',
  DECKEL_OEFFNEN: 'Deckel per App geöffnet',
  PAPIER_LEER: 'Papier leer',
  STOERUNG: 'Störung',
  GETRENNT: 'Drucker getrennt'
});

// bekannte Arten in fester Folge (Auswahllisten): Verkauf, Storno, Drucke ohne Verkauf, Positionsmeldungen, Zustände
export const KNOWN_KINDS = Object.freeze([].concat(
  Object.keys(SALE_KINDS), Object.keys(PRINT_TYPES), Object.keys(POSITION_TYPES), Object.keys(STATE_TYPES)
));

// Farbgebung im Druckprotokoll (Kundenwunsch 05.11.2026: Zustände nicht alle grau, „grün oder rot … und gelb für
// Warnungen“): Drucker bereit grün, Verbindung weg bzw. Störung rot, Hinweise gelb
export const OK_KINDS = Object.freeze(['VERBINDUNG', 'PAPIEREINZUG']);
export const ERROR_KINDS = Object.freeze(['GETRENNT', 'STOERUNG']);
export const WARNING_KINDS = Object.freeze(['PAPIER_LEER', 'DECKEL_OFFEN', 'DECKEL_OEFFNEN']);

// Gruppen des Art-Filters im Druckprotokoll; „Druckerzustand“ umfasst Positionsmeldungen und Zustände
export const KIND_GROUPS = Object.freeze([
  Object.freeze({ key: 'sale', text: 'Verkäufe', kinds: Object.freeze(Object.keys(SALE_KINDS)) }),
  Object.freeze({ key: 'print', text: 'Drucke ohne Verkauf', kinds: Object.freeze(Object.keys(PRINT_TYPES)) }),
  Object.freeze({
    key: 'state', text: 'Druckerzustand', kinds: Object.freeze(Object.keys(POSITION_TYPES).concat(Object.keys(STATE_TYPES)))
  })
]);

function has(table, kind) {
  return typeof kind === 'string' && Object.prototype.hasOwnProperty.call(table, kind);
}

// Druck ohne Verkauf, der Papier verbraucht (Rollenauswertung: belegt Abschnitte bzw. erklärt Lücken)
export function isPaperPrint(kind) {
  return has(PRINT_TYPES, kind);
}

// Positionsmeldung der GeBE-Rolle (Rollenauswertung: Einlage, Papiervorschub)
export function isPosition(kind) {
  return has(POSITION_TYPES, kind);
}

// 'sale' | 'print' | 'state' | 'other' (unbekannte, künftige Art)
export function kindGroup(kind) {
  if (has(SALE_KINDS, kind)) {
    return 'sale';
  }
  if (has(PRINT_TYPES, kind)) {
    return 'print';
  }
  return has(POSITION_TYPES, kind) || has(STATE_TYPES, kind) ? 'state' : 'other';
}

// lesbares Etikett; unbekannte Arten aus dem Code („NEUE_ART“ → „Neue art“), leer → „Druck“
export function kindLabel(kind) {
  if (has(SALE_KINDS, kind)) {
    return SALE_KINDS[kind];
  }
  if (has(PRINT_TYPES, kind)) {
    return PRINT_TYPES[kind];
  }
  if (has(POSITION_TYPES, kind)) {
    return POSITION_TYPES[kind];
  }
  if (has(STATE_TYPES, kind)) {
    return STATE_TYPES[kind];
  }
  const text = String(kind || '').trim().replace(/_/g, ' ').toLowerCase();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : 'Druck';
}

// Farbton der Kennung: Verkauf neutral, Storno rot, Druck ohne Verkauf blau, Drucker bereit grün, Drucker getrennt
// bzw. Störung rot, Warnung gelb, übrige (unbekannte) Zustände grau
export function kindTone(kind) {
  if (kind === 'STORNO') {
    return 'storno';
  }
  if (ERROR_KINDS.indexOf(kind) !== -1) {
    return 'error';
  }
  if (WARNING_KINDS.indexOf(kind) !== -1) {
    return 'warn';
  }
  if (OK_KINDS.indexOf(kind) !== -1) {
    return 'ok';
  }
  const group = kindGroup(kind);
  return group === 'other' ? 'state' : group;
}
